import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TRPCClientError } from "@trpc/client";
import { trpc } from "../lib/trpc.js";
import { NavActions } from "../components/NavActions.js";
import { Button } from "../components/ui/Button.js";
import { Skeleton } from "../components/ui/Skeleton.js";
import { ClubProgressBar } from "../components/ui/ClubProgressBar.js";
import { ConfirmDialog } from "../components/ui/ConfirmDialog.js";
import { PromoteDialog } from "../features/books/PromoteDialog.js";
import { DeadlineEditDialog } from "../features/books/DeadlineEditDialog.js";
import { BookSearchDialog } from "../features/books/BookSearchDialog.js";
import { InvitePanel } from "../features/invites/InvitePanel.js";
import { PollCard } from "../features/polls/PollCard.js";
import { LocationAutocomplete } from "../components/ui/LocationAutocomplete.js";

function PriorityPicker({ clubBookId, current }: { clubBookId: string; current: number | null }) {
  const utils = trpc.useUtils();
  const setPriority = trpc.clubBooks.setPriority.useMutation({
    onSuccess: () => utils.clubBooks.list.invalidate(),
  });

  return (
    <select
      className="text-xs rounded px-2 py-1 transition-all"
      style={{
        border: "1px solid var(--color-border)",
        background: "var(--color-background)",
        color: "var(--color-foreground)",
      }}
      value={current ?? ""}
      onChange={(e) => {
        const v = Number(e.target.value);
        if (v >= 1 && v <= 10) setPriority.mutate({ clubBookId, priority: v });
      }}
    >
      <option value="">Rate</option>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
        <option key={n} value={n}>{n}</option>
      ))}
    </select>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-xs font-medium uppercase tracking-widest mb-3"
      style={{ color: "var(--color-accent)" }}
    >
      {children}
    </p>
  );
}

// ─── Meetings ────────────────────────────────────────────────────────────────

type MeetingData = {
  id: string;
  title: string;
  dateTimeUtc: Date | string;
  durationMinutes?: number | null;
  location?: string | null;
  meetingLink?: string | null;
  description?: string | null;
  cancelledAt?: Date | string | null;
  createdByUserId: string;
  linkedClubBookId?: string | null;
};

function MeetingCard({
  meeting,
  viewerId,
  isAdmin,
  clubId,
  onEdit,
  onCancelled,
}: {
  meeting: MeetingData;
  viewerId: string;
  isAdmin: boolean;
  clubId: string;
  onEdit: (m: MeetingData) => void;
  onCancelled: () => void;
}) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const cancelMeeting = trpc.meetings.cancel.useMutation({
    onSuccess: () => {
      utils.meetings.list.invalidate({ clubId });
      onCancelled();
    },
  });

  const dt = new Date(meeting.dateTimeUtc);
  const dateStr = dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const timeStr = dt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const canManage = isAdmin || meeting.createdByUserId === viewerId;
  const isCancelled = !!meeting.cancelledAt;

  return (
    <div
      className="rounded-xl px-5 py-4 flex flex-col gap-2"
      style={{
        background: "var(--color-card)",
        border: "1px solid var(--color-border)",
        opacity: isCancelled ? 0.65 : 1,
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-serif text-base" style={{ color: "var(--color-foreground)" }}>
              {meeting.title}
            </span>
            {isCancelled && (
              <span
                className="text-xs font-medium uppercase tracking-widest px-2 py-0.5 rounded-full"
                style={{ background: "var(--color-accent-subtle)", color: "var(--color-accent-subtle-foreground)" }}
              >
                {t("meetings.cancelled")}
              </span>
            )}
          </div>
          <span className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
            {dateStr} · {timeStr}
            {meeting.durationMinutes ? ` · ${meeting.durationMinutes}min` : ""}
          </span>
        </div>
        {canManage && !isCancelled && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => onEdit(meeting)}
              className="text-xs px-3 py-1 rounded-lg transition-colors"
              style={{ border: "1px solid var(--color-border)", color: "var(--color-muted-foreground)", background: "transparent" }}
            >
              {t("meetings.edit")}
            </button>
            <button
              onClick={() => {
                if (window.confirm(t("meetings.cancelConfirm"))) {
                  cancelMeeting.mutate({ meetingId: meeting.id, clubId });
                }
              }}
              className="text-xs px-3 py-1 rounded-lg transition-colors"
              style={{ border: "1px solid var(--color-border)", color: "var(--color-muted-foreground)", background: "transparent" }}
            >
              {t("meetings.cancelMeeting")}
            </button>
          </div>
        )}
      </div>
      {(meeting.location || meeting.meetingLink) && (
        <div className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
          {meeting.location && <span>📍 {meeting.location}</span>}
          {meeting.meetingLink && (
            <a href={meeting.meetingLink} target="_blank" rel="noopener noreferrer"
              className="underline"
              style={{ color: "var(--color-accent)" }}
            >
              {meeting.meetingLink}
            </a>
          )}
        </div>
      )}
      {meeting.description && (
        <p className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>{meeting.description}</p>
      )}
    </div>
  );
}

function MeetingCreateForm({
  clubId,
  clubBooks,
  onSaved,
  onCancel,
  initialData,
}: {
  clubId: string;
  clubBooks: { id: string; title: string }[];
  onSaved: () => void;
  onCancel: () => void;
  initialData?: MeetingData | null;
}) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const [title, setTitle] = useState(initialData?.title ?? "");
  const [dateTime, setDateTime] = useState(() => {
    if (initialData?.dateTimeUtc) {
      const d = new Date(initialData.dateTimeUtc);
      return d.toISOString().slice(0, 16);
    }
    return "";
  });
  const [duration, setDuration] = useState(initialData?.durationMinutes?.toString() ?? "");
  const [location, setLocation] = useState(initialData?.location ?? "");
  const [meetingLink, setMeetingLink] = useState(initialData?.meetingLink ?? "");
  const [description, setDescription] = useState(initialData?.description ?? "");
  const [linkedBookId, setLinkedBookId] = useState(initialData?.linkedClubBookId ?? "");
  const [errors, setErrors] = useState<{ title?: string; dateTime?: string }>({});

  const createMeeting = trpc.meetings.create.useMutation({
    onSuccess: () => { utils.meetings.list.invalidate({ clubId }); onSaved(); },
  });
  const editMeeting = trpc.meetings.edit.useMutation({
    onSuccess: () => { utils.meetings.list.invalidate({ clubId }); onSaved(); },
  });

  function validate() {
    const e: typeof errors = {};
    if (!title.trim()) e.title = t("meetings.form.titleRequired");
    if (!dateTime) e.dateTime = t("meetings.form.dateTimeRequired");
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!validate()) return;
    const payload = {
      title: title.trim(),
      dateTimeUtc: new Date(dateTime),
      durationMinutes: duration ? parseInt(duration, 10) : undefined,
      location: location || undefined,
      meetingLink: meetingLink || undefined,
      description: description || undefined,
      linkedClubBookId: linkedBookId || undefined,
    };
    if (initialData) {
      editMeeting.mutate({ meetingId: initialData.id, clubId, ...payload });
    } else {
      createMeeting.mutate({ clubId, ...payload });
    }
  }

  const isPending = createMeeting.isPending || editMeeting.isPending;

  return (
    <form onSubmit={handleSubmit} className="rounded-xl px-5 py-5 flex flex-col gap-4"
      style={{ background: "var(--color-card)", border: "1px solid var(--color-border-strong)" }}>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium uppercase tracking-widest" style={{ color: "var(--color-muted-foreground)" }}>
          {t("meetings.form.title")} *
        </label>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder={t("meetings.form.titlePlaceholder")}
          className="rounded-lg px-3 py-2 text-sm"
          style={{ background: "var(--color-background)", border: `1px solid ${errors.title ? "var(--color-accent)" : "var(--color-border)"}`, color: "var(--color-foreground)" }}
        />
        {errors.title && <span className="text-xs" style={{ color: "var(--color-accent)" }}>{errors.title}</span>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium uppercase tracking-widest" style={{ color: "var(--color-muted-foreground)" }}>
            {t("meetings.form.dateTime")} *
          </label>
          <input
            type="datetime-local"
            value={dateTime}
            onChange={e => setDateTime(e.target.value)}
            className="rounded-lg px-3 py-2 text-sm"
            style={{ background: "var(--color-background)", border: `1px solid ${errors.dateTime ? "var(--color-accent)" : "var(--color-border)"}`, color: "var(--color-foreground)" }}
          />
          {errors.dateTime && <span className="text-xs" style={{ color: "var(--color-accent)" }}>{errors.dateTime}</span>}
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium uppercase tracking-widest" style={{ color: "var(--color-muted-foreground)" }}>
            {t("meetings.form.duration")}
          </label>
          <input
            type="number"
            min={1}
            value={duration}
            onChange={e => setDuration(e.target.value)}
            placeholder={t("meetings.form.durationPlaceholder")}
            className="rounded-lg px-3 py-2 text-sm"
            style={{ background: "var(--color-background)", border: "1px solid var(--color-border)", color: "var(--color-foreground)" }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium uppercase tracking-widest" style={{ color: "var(--color-muted-foreground)" }}>
            {t("meetings.form.location")}
          </label>
          <input
            value={location}
            onChange={e => setLocation(e.target.value)}
            placeholder={t("meetings.form.locationPlaceholder")}
            className="rounded-lg px-3 py-2 text-sm"
            style={{ background: "var(--color-background)", border: "1px solid var(--color-border)", color: "var(--color-foreground)" }}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium uppercase tracking-widest" style={{ color: "var(--color-muted-foreground)" }}>
            {t("meetings.form.meetingLink")}
          </label>
          <input
            value={meetingLink}
            onChange={e => setMeetingLink(e.target.value)}
            placeholder={t("meetings.form.meetingLinkPlaceholder")}
            className="rounded-lg px-3 py-2 text-sm"
            style={{ background: "var(--color-background)", border: "1px solid var(--color-border)", color: "var(--color-foreground)" }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium uppercase tracking-widest" style={{ color: "var(--color-muted-foreground)" }}>
          {t("meetings.form.description")}
        </label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder={t("meetings.form.descriptionPlaceholder")}
          rows={3}
          maxLength={500}
          className="rounded-lg px-3 py-2 text-sm resize-none"
          style={{ background: "var(--color-background)", border: "1px solid var(--color-border)", color: "var(--color-foreground)" }}
        />
      </div>

      {clubBooks.length > 0 && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium uppercase tracking-widest" style={{ color: "var(--color-muted-foreground)" }}>
            {t("meetings.form.linkedBook")}
          </label>
          <select
            value={linkedBookId}
            onChange={e => setLinkedBookId(e.target.value)}
            className="rounded-lg px-3 py-2 text-sm"
            style={{ background: "var(--color-background)", border: "1px solid var(--color-border)", color: "var(--color-foreground)" }}
          >
            <option value="">{t("meetings.form.linkedBookNone")}</option>
            {clubBooks.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
          </select>
        </div>
      )}

      <div className="flex items-center gap-2 opacity-50 cursor-not-allowed select-none" title={t("meetings.form.recurringComingSoon")}>
        <input type="checkbox" disabled className="rounded" />
        <span className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>{t("meetings.form.recurring")}</span>
      </div>

      <div className="flex items-center gap-3 pt-1">
        <Button type="submit" disabled={isPending} className="text-sm px-5 py-2">
          {t("meetings.form.save")}
        </Button>
        <button type="button" onClick={onCancel}
          className="text-sm px-4 py-2 rounded-lg transition-colors"
          style={{ color: "var(--color-muted-foreground)", border: "1px solid var(--color-border)", background: "transparent" }}>
          {t("meetings.form.cancel")}
        </button>
      </div>
    </form>
  );
}

function MeetingsSection({
  clubId,
  viewerId,
  isAdmin,
  clubBooks,
}: {
  clubId: string;
  viewerId: string;
  isAdmin: boolean;
  clubBooks: { id: string; title: string }[];
}) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<MeetingData | null>(null);

  const { data: meetings = [] } = trpc.meetings.list.useQuery({ clubId });

  function handleEdit(m: MeetingData) {
    setEditingMeeting(m);
    setShowForm(true);
  }

  function handleSaved() {
    setShowForm(false);
    setEditingMeeting(null);
  }

  return (
    <section className="px-6 pb-10 max-w-4xl mx-auto mt-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs font-medium uppercase tracking-widest" style={{ color: "var(--color-muted-foreground)" }}>
          {t("meetings.sectionTitle")}
        </h2>
        {!showForm && (
          <button
            onClick={() => { setEditingMeeting(null); setShowForm(true); }}
            className="text-xs px-3 py-1.5 rounded-lg transition-colors"
            style={{ border: "1px solid var(--color-border)", color: "var(--color-accent)", background: "transparent" }}
          >
            {t("meetings.scheduleMeeting")}
          </button>
        )}
      </div>

      {showForm && (
        <div className="mb-4">
          <MeetingCreateForm
            clubId={clubId}
            clubBooks={clubBooks}
            onSaved={handleSaved}
            onCancel={() => { setShowForm(false); setEditingMeeting(null); }}
            initialData={editingMeeting}
          />
        </div>
      )}

      {meetings.length === 0 && !showForm ? (
        <p className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>{t("meetings.noUpcoming")}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {meetings.map((m: MeetingData) => (
            <MeetingCard
              key={m.id}
              meeting={m}
              viewerId={viewerId}
              isAdmin={isAdmin}
              clubId={clubId}
              onEdit={handleEdit}
              onCancelled={() => {}}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ClubDetailsSection({
  isAdmin,
  isEditing,
  onEditStart,
  onEditCancel,
  onSave,
  description,
  type,
  locationCity,
  locationCountry,
}: {
  isAdmin: boolean;
  isEditing: boolean;
  onEditStart: () => void;
  onEditCancel: () => void;
  onSave: (data: { description: string | null; type: string | null; locationCity: string | null; locationCountry: string | null }) => void;
  description: string | null;
  type: string | null;
  locationCity: string | null;
  locationCountry: string | null;
}) {
  const [descDraft, setDescDraft] = useState(description ?? "");
  const [typeDraft, setTypeDraft] = useState(type ?? "");
  const [cityDraft, setCityDraft] = useState(locationCity ?? "");
  const [countryDraft, setCountryDraft] = useState(locationCountry ?? "");

  const handleEditStart = () => {
    setDescDraft(description ?? "");
    setTypeDraft(type ?? "");
    setCityDraft(locationCity ?? "");
    setCountryDraft(locationCountry ?? "");
    onEditStart();
  };

  const handleSave = () => {
    onSave({
      description: descDraft.trim() || null,
      type: typeDraft || null,
      locationCity: cityDraft.trim() || null,
      locationCountry: countryDraft.trim() || null,
    });
  };

  const typeLabel: Record<string, string> = {
    IN_PERSON: "In person",
    ONLINE: "Online",
    BOTH: "Both",
  };

  const hasLocation = locationCity || locationCountry;

  if (!isEditing) {
    return (
      <div
        style={{
          animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both",
          borderTop: "1px solid var(--color-border)",
          paddingTop: "2.5rem",
          marginTop: "2.5rem",
        }}
      >
        <div className="flex items-center justify-between mb-5">
          <p
            className="text-xs font-medium uppercase tracking-widest"
            style={{ color: "var(--color-accent)" }}
          >
            About this club
          </p>
          {isAdmin && (
            <button
              onClick={handleEditStart}
              className="flex items-center gap-1 text-xs transition-colors"
              style={{ color: "var(--color-muted-foreground)", background: "none", border: "none", cursor: "pointer", padding: "0.25rem 0.5rem", borderRadius: "0.375rem" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--color-accent)";
                e.currentTarget.style.background = "var(--color-accent-subtle)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--color-muted-foreground)";
                e.currentTarget.style.background = "none";
              }}
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 0 1-.927-.928l.929-3.25c.081-.286.235-.547.445-.758l8.61-8.61Z" fill="currentColor" opacity="0.8" />
              </svg>
              Edit details
            </button>
          )}
        </div>

        <div
          className="rounded-xl relative overflow-hidden"
          style={{
            background: "var(--color-card)",
            border: "1px solid var(--color-border)",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: "1.5rem",
              bottom: "1.5rem",
              width: "3px",
              background: "var(--color-accent)",
              borderRadius: "0 2px 2px 0",
              opacity: 0.55,
            }}
          />
          <div style={{ padding: "1.75rem 1.75rem 1.75rem 2.25rem" }}>
            <p
              className="font-serif"
              style={{
                fontSize: "1.0625rem",
                lineHeight: "1.75",
                color: description ? "var(--color-foreground)" : "var(--color-muted-foreground)",
                fontStyle: description ? "normal" : "italic",
                letterSpacing: description ? "-0.01em" : "normal",
              }}
            >
              {description || "No description yet."}
            </p>

            {(type || hasLocation) && (
              <div
                className="flex flex-wrap items-center gap-3 mt-5 pt-4"
                style={{ borderTop: "1px solid var(--color-border)" }}
              >
                {type && (
                  <div className="flex items-center gap-1.5">
                    <span
                      className="text-xs font-medium uppercase tracking-widest"
                      style={{ color: "var(--color-muted-foreground)", opacity: 0.65 }}
                    >
                      Club type
                    </span>
                    <span
                      className="text-xs font-medium rounded-full px-2.5 py-0.5"
                      style={{
                        background: "var(--color-accent-subtle)",
                        color: "var(--color-accent-subtle-foreground)",
                        letterSpacing: "0.01em",
                      }}
                    >
                      {typeLabel[type]}
                    </span>
                  </div>
                )}
                {type && hasLocation && (
                  <span style={{ width: "1px", height: "1rem", background: "var(--color-border-strong)", opacity: 0.5, display: "inline-block", flexShrink: 0 }} />
                )}
                {hasLocation && (
                  <div className="flex items-center gap-1.5">
                    <span
                      className="text-xs font-medium uppercase tracking-widest"
                      style={{ color: "var(--color-muted-foreground)", opacity: 0.65 }}
                    >
                      Location
                    </span>
                    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ color: "var(--color-muted-foreground)", opacity: 0.7, flexShrink: 0 }}>
                      <path fillRule="evenodd" clipRule="evenodd" d="M8 1.5a4.5 4.5 0 0 0-4.5 4.5c0 2.44 1.564 4.627 3.11 6.223A22.8 22.8 0 0 0 8 13.653a22.8 22.8 0 0 0 1.39-1.43C10.936 10.627 12.5 8.44 12.5 6A4.5 4.5 0 0 0 8 1.5ZM2 6a6 6 0 1 1 12 0c0 2.96-1.814 5.42-3.443 7.115A24.3 24.3 0 0 1 8 15.25a24.3 24.3 0 0 1-2.557-2.135C3.814 11.42 2 8.96 2 6Zm6 1.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" fill="currentColor" />
                    </svg>
                    <span className="text-xs" style={{ color: "var(--color-foreground)", opacity: 0.8 }}>
                      {[locationCity, locationCountry].filter(Boolean).join(", ")}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) both",
        borderTop: "1px solid var(--color-border)",
        paddingTop: "2.5rem",
        marginTop: "2.5rem",
      }}
    >
      <p
        className="text-xs font-medium uppercase tracking-widest mb-5"
        style={{ color: "var(--color-accent)" }}
      >
        About this club
      </p>
      <div
        className="rounded-xl space-y-5"
        style={{
          background: "var(--color-card)",
          border: "1px solid var(--color-border)",
          boxShadow: "var(--shadow-sm)",
          padding: "1.5rem",
        }}
      >
        <div>
          <label
            className="text-xs font-medium uppercase tracking-widest block mb-2"
            style={{ color: "var(--color-muted-foreground)" }}
          >
            Description
          </label>
          <textarea
            maxLength={280}
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
            placeholder="What's this club about?"
            className="w-full rounded-lg text-sm transition-all focus-visible:outline-none resize-none"
            style={{
              border: "1px solid var(--color-border)",
              background: "var(--color-background)",
              color: "var(--color-foreground)",
              padding: "0.625rem 0.75rem",
              minHeight: "6rem",
              lineHeight: "1.65",
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = "var(--color-border-strong)")}
            onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
          />
          <p
            className="text-xs mt-1 text-right tabular-nums"
            style={{
              color: descDraft.length > 250 ? "var(--color-accent)" : "var(--color-muted-foreground)",
              opacity: descDraft.length > 250 ? 1 : 0.7,
            }}
          >
            {descDraft.length}/280
          </p>
        </div>

        <div>
          <label
            className="text-xs font-medium uppercase tracking-widest block mb-2"
            style={{ color: "var(--color-muted-foreground)" }}
          >
            Club type
          </label>
          <select
            value={typeDraft}
            onChange={(e) => setTypeDraft(e.target.value)}
            className="rounded-lg text-sm transition-all focus-visible:outline-none"
            style={{
              border: "1px solid var(--color-border)",
              background: "var(--color-background)",
              color: typeDraft ? "var(--color-foreground)" : "var(--color-muted-foreground)",
              padding: "0.5rem 0.75rem",
              height: "2.25rem",
              minWidth: "10rem",
              appearance: "none",
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 16 16'%3E%3Cpath fill='%238a8070' d='M4.427 6.427l3.396 3.396a.25.25 0 0 0 .354 0l3.396-3.396A.25.25 0 0 0 11.396 6H4.604a.25.25 0 0 0-.177.427Z'/%3E%3C/svg%3E")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 0.6rem center",
              paddingRight: "2rem",
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = "var(--color-border-strong)")}
            onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
          >
            <option value="">— Not specified —</option>
            <option value="IN_PERSON">In person</option>
            <option value="ONLINE">Online</option>
            <option value="BOTH">Both</option>
          </select>
        </div>

        <div>
          <p
            className="text-xs font-medium uppercase tracking-widest mb-2"
            style={{ color: "var(--color-muted-foreground)" }}
          >
            Location
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-3" style={{ maxWidth: "28rem" }}>
            <div className="flex-1">
              <label
                className="text-xs block mb-1"
                style={{ color: "var(--color-muted-foreground)", opacity: 0.75 }}
              >
                City
              </label>
              <LocationAutocomplete
                type="city"
                value={cityDraft}
                onChange={setCityDraft}
                placeholder="e.g. Lisbon"
              />
            </div>
            <div className="flex-1">
              <label
                className="text-xs block mb-1"
                style={{ color: "var(--color-muted-foreground)", opacity: 0.75 }}
              >
                Country
              </label>
              <LocationAutocomplete
                type="country"
                value={countryDraft}
                onChange={setCountryDraft}
                placeholder="e.g. Portugal"
              />
            </div>
          </div>
        </div>

        <div
          className="flex items-center gap-2 pt-1"
          style={{ borderTop: "1px solid var(--color-border)" }}
        >
          <button
            onClick={handleSave}
            className="px-4 py-1.5 rounded-lg text-sm font-medium transition-all"
            style={{ background: "var(--color-accent)", color: "white", border: "none", cursor: "pointer" }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            Save
          </button>
          <button
            onClick={onEditCancel}
            className="px-4 py-1.5 rounded-lg text-sm font-medium transition-all"
            style={{ background: "var(--color-secondary)", color: "var(--color-muted-foreground)", border: "none", cursor: "pointer" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-muted)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "var(--color-secondary)")}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function ClubView() {
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showSearch, setShowSearch] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [promoteTarget, setPromoteTarget] = useState<{ id: string; title: string; pageCount: number | null } | null>(null);
  const [deadlineEditTarget, setDeadlineEditTarget] = useState<{ id: string; deadline: Date | string | null } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string; message?: string; destructive?: boolean; onConfirm: () => void;
  } | null>(null);

  const { data: club, isLoading: clubLoading, error: clubError } = trpc.clubs.get.useQuery(
    { clubId: clubId! },
    { enabled: !!clubId }
  );
  const { data: clubBooks, isLoading: booksLoading } = trpc.clubBooks.list.useQuery(
    { clubId: clubId! },
    { enabled: !!clubId }
  );

  const { data: me } = trpc.auth.me.useQuery();
  const utils = trpc.useUtils();

  const { data: joinRequests } = trpc.joinRequests.listForClub.useQuery(
    { clubId: clubId! },
    { enabled: !!clubId && club?.viewerRole === "ADMIN", refetchInterval: 3000 }
  );

  const leaveClub = trpc.clubs.leave.useMutation({
    onSuccess: () => navigate("/dashboard"),
  });
  const kickMember = trpc.clubs.kickMember.useMutation({
    onSuccess: () => utils.clubs.get.invalidate({ clubId }),
  });
  const promoteToAdmin = trpc.clubs.promoteToAdmin.useMutation({
    onSuccess: () => utils.clubs.get.invalidate({ clubId }),
  });
  const demoteToMember = trpc.clubs.demoteToMember.useMutation({
    onSuccess: () => utils.clubs.get.invalidate({ clubId }),
  });
  const setType = trpc.clubs.setType.useMutation({
    onMutate: (vars) => {
      const prev = utils.clubs.get.getData({ clubId: clubId! });
      utils.clubs.get.setData({ clubId: clubId! }, (old) =>
        old ? { ...old, type: vars.type } : undefined
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) utils.clubs.get.setData({ clubId: clubId! }, ctx.prev);
    },
  });

  const setLocation = trpc.clubs.setLocation.useMutation({
    onMutate: (vars) => {
      const prev = utils.clubs.get.getData({ clubId: clubId! });
      utils.clubs.get.setData({ clubId: clubId! }, (old) =>
        old ? { ...old, locationCity: vars.locationCity, locationCountry: vars.locationCountry } : undefined
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) utils.clubs.get.setData({ clubId: clubId! }, ctx.prev);
    },
  });

  const setDescription = trpc.clubs.setDescription.useMutation({
    onMutate: (vars) => {
      const prev = utils.clubs.get.getData({ clubId: clubId! });
      utils.clubs.get.setData({ clubId: clubId! }, (old) =>
        old ? { ...old, description: vars.description } : undefined
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) utils.clubs.get.setData({ clubId: clubId! }, ctx.prev);
    },
  });

  const approveRequest = trpc.joinRequests.approve.useMutation({
    onSuccess: () => {
      utils.joinRequests.listForClub.invalidate({ clubId });
      utils.clubs.get.invalidate({ clubId });
    },
  });

  const declineRequest = trpc.joinRequests.decline.useMutation({
    onSuccess: () => {
      utils.joinRequests.listForClub.invalidate({ clubId });
    },
  });
  function optimisticBookState(targetId: string, newState: "CURRENT" | "ARCHIVE" | null) {
    const key = { clubId: clubId! };
    utils.clubBooks.list.setData(key, (old) =>
      newState === null
        ? old?.filter((cb) => cb.id !== targetId)
        : old?.map((cb) => cb.id === targetId ? { ...cb, state: newState } : cb)
    );
  }

  const removeBook = trpc.clubBooks.removeFromBucketList.useMutation({
    onMutate: (vars) => {
      const prev = utils.clubBooks.list.getData({ clubId: clubId! });
      optimisticBookState(vars.clubBookId, null);
      return { prev };
    },
    onError: (_err, _vars, ctx) => { if (ctx?.prev) utils.clubBooks.list.setData({ clubId: clubId! }, ctx.prev); },
    onSettled: () => utils.clubBooks.list.invalidate({ clubId }),
  });
  const promote = trpc.clubBooks.promote.useMutation({
    onMutate: (vars) => {
      const prev = utils.clubBooks.list.getData({ clubId: clubId! });
      optimisticBookState(vars.clubBookId, "CURRENT");
      return { prev };
    },
    onError: (_err, _vars, ctx) => { if (ctx?.prev) utils.clubBooks.list.setData({ clubId: clubId! }, ctx.prev); },
    onSettled: () => utils.clubBooks.list.invalidate({ clubId }),
  });
  const demoteBook = trpc.clubBooks.demote.useMutation({
    onMutate: (vars) => {
      const prev = utils.clubBooks.list.getData({ clubId: clubId! });
      optimisticBookState(vars.clubBookId, "BUCKET" as never);
      return { prev };
    },
    onError: (_err, _vars, ctx) => { if (ctx?.prev) utils.clubBooks.list.setData({ clubId: clubId! }, ctx.prev); },
    onSettled: () => utils.clubBooks.list.invalidate({ clubId }),
  });
  const archiveBook = trpc.clubBooks.archive.useMutation({
    onMutate: (vars) => {
      const prev = utils.clubBooks.list.getData({ clubId: clubId! });
      optimisticBookState(vars.clubBookId, "ARCHIVE");
      return { prev };
    },
    onError: (_err, _vars, ctx) => { if (ctx?.prev) utils.clubBooks.list.setData({ clubId: clubId! }, ctx.prev); },
    onSettled: () => utils.clubBooks.list.invalidate({ clubId }),
  });
  const setDeadline = trpc.clubBooks.setDeadline.useMutation({
    onSettled: () => utils.clubBooks.list.invalidate({ clubId }),
  });

  if (clubLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center gap-2"
        style={{ background: "var(--color-background)" }}
      >
        <span
          className="h-4 w-4 rounded-full border-2 border-current border-t-transparent"
          style={{ color: "var(--color-accent)", animation: "spin 0.7s linear infinite" }}
        />
        <span className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>{t("common.loading")}</span>
      </div>
    );
  }

  if (clubError) {
    const code = clubError instanceof TRPCClientError ? clubError.data?.code : null;
    return (
      <div
        className="flex min-h-screen items-center justify-center flex-col gap-4"
        style={{ background: "var(--color-background)" }}
      >
        <p className="font-serif italic text-lg" style={{ color: "var(--color-muted-foreground)" }}>
          {code === "FORBIDDEN" ? "You're not a member of this club." : "Club not found."}
        </p>
        <Button variant="ghost" onClick={() => navigate("/dashboard")}>{t("nav.backToClubs")}</Button>
      </div>
    );
  }

  if (!club) return null;

  const isAdmin = club.viewerRole === "ADMIN";
  const current = clubBooks?.filter((cb) => cb.state === "CURRENT") ?? [];
  const bucket = clubBooks?.filter((cb) => cb.state === "BUCKET") ?? [];
  const archive = clubBooks?.filter((cb) => cb.state === "ARCHIVE") ?? [];

  return (
    <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
      {/* Header */}
      <header
        className="sticky top-0 z-10 flex items-center gap-3 px-6 py-4 md:px-10"
        style={{
          background: "oklch(97% 0.014 82 / 0.92)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        <Link
          to="/dashboard"
          className="text-sm flex-shrink-0"
          style={{ color: "var(--color-muted-foreground)", textDecoration: "none" }}
        >
          {t("nav.backToClubs")}
        </Link>
        <span style={{ color: "var(--color-border-strong)" }}>|</span>
        <span
          className="font-serif font-semibold truncate"
          style={{ color: "var(--color-foreground)" }}
        >
          {club.name}
        </span>
        <span
          className="rounded-full px-2 py-0.5 text-xs flex-shrink-0"
          style={{
            background: "var(--color-secondary)",
            color: "var(--color-muted-foreground)",
          }}
        >
          {club.visibility === "PRIVATE" ? t("club.visibility.private") : t("club.visibility.public")}
        </span>
        {club.type && (
          <span
            className="rounded-full px-2 py-0.5 text-xs flex-shrink-0"
            style={{
              background: "var(--color-muted)",
              color: "var(--color-muted-foreground)",
            }}
          >
            {t(`clubType.${club.type}`)}
          </span>
        )}
        {(club.locationCity || club.locationCountry) && (
          <span
            className="text-xs flex-shrink-0"
            style={{ color: "var(--color-muted-foreground)" }}
          >
            {[club.locationCity, club.locationCountry].filter(Boolean).join(", ")}
          </span>
        )}
        {isAdmin && (
          <span
            className="rounded-full px-2 py-0.5 text-xs flex-shrink-0"
            style={{
              background: "var(--color-accent-subtle)",
              color: "var(--color-accent-subtle-foreground)",
            }}
          >
            {t("club.roleAdmin")}
          </span>
        )}
        <div className="ml-auto"><NavActions /></div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10 md:px-10">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
          {/* Books column */}
          <section className="md:col-span-2 space-y-10">

          {/* Currently reading */}
          <div style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.05s both" }}>
            <SectionLabel>{t("club.sections.currentlyReading")}</SectionLabel>
            {booksLoading ? (
              <div className="space-y-2">
                {[...Array(1)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4 rounded-xl px-4 py-3.5" style={{ background: "var(--color-card)", border: "1px solid var(--color-border)" }}>
                    <Skeleton style={{ width: "2.25rem", height: "3rem", flexShrink: 0 }} />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton style={{ width: "60%", height: "0.85rem" }} />
                      <Skeleton style={{ width: "40%", height: "0.75rem" }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : current.length === 0 ? (
              <p className="text-sm italic" style={{ color: "var(--color-muted-foreground)" }}>
                {t("club.books.nothingInProgress")}
              </p>
            ) : (
              <ul className="space-y-2">
                {current.map((cb) => (
                  <li
                    key={cb.id}
                    className="book-spine flex items-center gap-4 rounded-xl px-4 py-3.5"
                    style={{
                      background: "var(--color-card)",
                      border: "1px solid var(--color-border)",
                      boxShadow: "var(--shadow-sm)",
                    }}
                  >
                    {cb.book.coverUrl ? (
                      <img
                        src={cb.book.coverUrl}
                        alt=""
                        className="w-9 h-12 object-cover rounded flex-shrink-0"
                        style={{ background: "var(--color-muted)" }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                    ) : (
                      <div
                        className="w-9 h-12 rounded flex-shrink-0"
                        style={{ background: "var(--color-muted)" }}
                      />
                    )}
                    <div className="flex-1 min-w-0 pl-1">
                      <p
                        className="font-serif font-medium text-sm truncate"
                        style={{ color: "var(--color-foreground)" }}
                      >
                        {cb.book.title}
                      </p>
                      <p className="text-xs truncate mt-0.5" style={{ color: "var(--color-muted-foreground)" }}>
                        {cb.book.authors.join(", ")}
                      </p>
                      {cb.memberProgress.length > 0 && (
                        <div className="mt-2 pr-1">
                          <ClubProgressBar
                            members={cb.memberProgress}
                            totalPages={cb.canonicalPageCount}
                            addedAt={cb.addedAt}
                            deadline={cb.deadline}
                            isAdmin={isAdmin}
                            onEditDeadline={() => setDeadlineEditTarget({ id: cb.id, deadline: cb.deadline })}
                            size="compact"
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isAdmin && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            loading={demoteBook.isPending && demoteBook.variables?.clubBookId === cb.id}
                            onClick={() => setConfirmDialog({
                              title: t("club.books.backToBucket"),
                              message: t("club.books.confirmBackToBucket"),
                              onConfirm: () => demoteBook.mutate({ clubBookId: cb.id }),
                            })}
                            className="text-xs"
                            style={{ color: "var(--color-muted-foreground)" }}
                          >
                            {t("club.books.backToBucket")}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            loading={archiveBook.isPending && archiveBook.variables?.clubBookId === cb.id}
                            onClick={() => setConfirmDialog({
                              title: t("club.books.finished"),
                              message: t("club.books.confirmFinished"),
                              onConfirm: () => archiveBook.mutate({ clubBookId: cb.id }),
                            })}
                            className="text-xs"
                            style={{ color: "var(--color-muted-foreground)" }}
                          >
                            {t("club.books.finished")}
                          </Button>
                        </>
                      )}
                      <Link
                        to={`/clubs/${clubId}/books/${cb.id}`}
                        className="text-xs flex-shrink-0 px-3 py-1.5 rounded-md font-medium transition-all"
                        style={{
                          color: "var(--color-accent)",
                          background: "var(--color-accent-subtle)",
                          textDecoration: "none",
                        }}
                      >
                        {t("club.books.openBook")}
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Bucket list */}
          <div style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both" }}>
            <div className="flex items-center justify-between mb-3">
              <SectionLabel>{t("club.sections.bucketList")}</SectionLabel>
              {isAdmin && (
                <Button size="sm" onClick={() => setShowSearch(true)}>{t("club.books.addBook")}</Button>
              )}
            </div>
            {booksLoading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ background: "var(--color-card)", border: "1px solid var(--color-border)" }}>
                    <Skeleton style={{ width: "2rem", height: "2.75rem", flexShrink: 0 }} />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton style={{ width: "55%", height: "0.85rem" }} />
                      <Skeleton style={{ width: "35%", height: "0.75rem" }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : bucket.length === 0 ? (
              <p className="text-sm italic" style={{ color: "var(--color-muted-foreground)" }}>
                {t("club.books.nothingQueued")}
              </p>
            ) : (
              <ul className="space-y-2">
                {bucket.map((cb) => (
                  <li
                    key={cb.id}
                    className="flex items-center gap-3 rounded-xl px-4 py-3"
                    style={{
                      background: "var(--color-card)",
                      border: "1px solid var(--color-border)",
                      boxShadow: "var(--shadow-xs)",
                    }}
                  >
                    {cb.book.coverUrl ? (
                      <img
                        src={cb.book.coverUrl}
                        alt=""
                        className="w-8 h-11 object-cover rounded flex-shrink-0"
                        style={{ background: "var(--color-muted)" }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-8 h-11 rounded flex-shrink-0" style={{ background: "var(--color-muted)" }} />
                    )}
                    <div className="flex-1 min-w-0">
                      <p
                        className="font-serif text-sm font-medium truncate"
                        style={{ color: "var(--color-foreground)" }}
                      >
                        {cb.book.title}
                      </p>
                      <p className="text-xs truncate" style={{ color: "var(--color-muted-foreground)" }}>
                        {cb.book.authors.join(", ")}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: "var(--color-muted-foreground)", opacity: 0.7 }}>
                        {t("club.books.ratingCount", { count: cb.priorityCount })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <PriorityPicker clubBookId={cb.id} current={cb.myPriority} />
                      {isAdmin && (
                        <>
                          <Button
                            size="sm"
                            loading={promote.isPending && promote.variables?.clubBookId === cb.id}
                            disabled={promote.isPending}
                            onClick={() => setPromoteTarget({ id: cb.id, title: cb.book.title, pageCount: cb.book.pageCount ?? null })}
                            className="text-xs"
                          >
                            {t("club.books.startReading")}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            loading={removeBook.isPending && removeBook.variables?.clubBookId === cb.id}
                            onClick={() => removeBook.mutate({ clubBookId: cb.id })}
                            className="text-xs"
                            style={{ color: "var(--color-destructive)" }}
                          >
                            {t("common.remove")}
                          </Button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Archive */}
          {archive.length > 0 && (
            <div style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.15s both" }}>
              <SectionLabel>{t("club.sections.readArchive")}</SectionLabel>
              <ul className="space-y-2 opacity-75">
                {archive.map((cb) => (
                  <li
                    key={cb.id}
                    className="flex items-center gap-3 rounded-xl px-4 py-3"
                    style={{
                      background: "var(--color-card)",
                      border: "1px solid var(--color-border)",
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <p
                        className="font-serif text-sm font-medium truncate"
                        style={{ color: "var(--color-muted-foreground)" }}
                      >
                        {cb.book.title}
                      </p>
                      <p className="text-xs truncate" style={{ color: "var(--color-muted-foreground)", opacity: 0.7 }}>
                        {cb.book.authors.join(", ")}
                      </p>
                      {cb.archivedAt && cb.deadline && (
                        <p className="text-xs mt-0.5" style={{ color: "var(--color-muted-foreground)", opacity: 0.8 }}>
                          {(() => {
                            const archived = new Date(cb.archivedAt).setHours(0, 0, 0, 0);
                            const due = new Date(cb.deadline).setHours(0, 0, 0, 0);
                            const diffDays = Math.round((archived - due) / (1000 * 60 * 60 * 24));
                            if (diffDays < 0) return t("club.description.finishedEarly_other", { count: Math.abs(diffDays) });
                            if (diffDays > 0) return t("club.description.finishedLate_other", { count: diffDays });
                            return t("club.description.finishedOnTime");
                          })()}
                        </p>
                      )}
                    </div>
                    <Link
                      to={`/clubs/${clubId}/books/${cb.id}`}
                      className="text-xs flex-shrink-0 px-3 py-1.5 rounded-md font-medium transition-all"
                      style={{
                        color: "var(--color-muted-foreground)",
                        background: "var(--color-secondary)",
                        textDecoration: "none",
                      }}
                    >
                      {t("club.books.openBook")}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Members sidebar */}
        <section className="space-y-6" style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.12s both" }}>

          {/* Poll */}
          <div>
            <SectionLabel>{t("club.sections.vote")}</SectionLabel>
            <PollCard clubId={clubId!} isAdmin={isAdmin} />
          </div>

          <div>
            <SectionLabel>{t("club.sections.members")} ({club.members.length})</SectionLabel>
            <ul className="space-y-1.5">
              {club.members.map((m) => {
                const isMe = m.userId === me?.id;
                const isOtherAdmin = m.role === "ADMIN" && !isMe;
                return (
                  <li
                    key={m.userId}
                    className="rounded-lg px-3 py-2.5 text-sm"
                    style={{ background: "var(--color-card)", border: "1px solid var(--color-border)" }}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span style={{ color: "var(--color-foreground)" }}>
                        {m.displayName}{isMe && <span className="ml-1 text-xs" style={{ color: "var(--color-muted-foreground)" }}>{t("club.youMarker")}</span>}
                      </span>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {m.role === "ADMIN" && (
                          <span className="text-xs rounded-full px-2 py-0.5" style={{ background: "var(--color-accent-subtle)", color: "var(--color-accent-subtle-foreground)" }}>
                            {t("club.roleAdmin")}
                          </span>
                        )}
                        {isAdmin && !isMe && (
                          <>
                            {m.role === "MEMBER" ? (
                              <button
                                className="text-xs px-1.5 py-0.5 rounded transition-colors"
                                style={{ color: "var(--color-muted-foreground)" }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-accent)")}
                                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
                                onClick={() => promoteToAdmin.mutate({ clubId: clubId!, userId: m.userId })}
                              >
                                {t("club.members.promote")}
                              </button>
                            ) : (
                              <button
                                className="text-xs px-1.5 py-0.5 rounded transition-colors"
                                style={{ color: "var(--color-muted-foreground)" }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
                                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
                                onClick={() => demoteToMember.mutate({ clubId: clubId!, userId: m.userId })}
                              >
                                {t("club.members.demote")}
                              </button>
                            )}
                            {!isOtherAdmin && (
                              <button
                                className="text-xs px-1.5 py-0.5 rounded transition-colors"
                                style={{ color: "var(--color-muted-foreground)" }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-destructive)")}
                                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
                                onClick={() => setConfirmDialog({
                                  title: t("club.members.remove"),
                                  message: t("club.members.confirmRemove", { name: m.displayName }),
                                  destructive: true,
                                  onConfirm: () => kickMember.mutate({ clubId: clubId!, userId: m.userId }),
                                })}
                              >
                                {t("club.members.remove")}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            <button
              className="mt-2 w-full text-xs py-1.5 rounded-lg transition-colors"
              style={{ color: "var(--color-muted-foreground)", border: "1px solid var(--color-border)" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "var(--color-destructive)"; e.currentTarget.style.borderColor = "var(--color-destructive)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-muted-foreground)"; e.currentTarget.style.borderColor = "var(--color-border)"; }}
              onClick={() => setConfirmDialog({
                title: t("club.members.leaveClub"),
                message: t("club.members.confirmLeave"),
                destructive: true,
                onConfirm: () => leaveClub.mutate({ clubId: clubId! }),
              })}
            >
              {leaveClub.isPending ? t("common.loading") : t("club.members.leaveClub")}
            </button>
          </div>

          {/* Join requests section — admins only */}
          {isAdmin && (joinRequests?.length ?? 0) > 0 && (
            <div>
              <SectionLabel>{t("joinRequest.requests")}</SectionLabel>
              <ul className="space-y-1.5">
                {joinRequests?.map((r) => (
                  <li
                    key={r.id}
                    className="rounded-lg px-3 py-2.5 text-sm"
                    style={{ background: "var(--color-card)", border: "1px solid var(--color-border)" }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p style={{ color: "var(--color-foreground)" }}>
                          {r.displayName}
                        </p>
                        <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
                          {new Date(r.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          size="sm"
                          loading={approveRequest.isPending && approveRequest.variables?.requestId === r.id}
                          disabled={approveRequest.isPending || declineRequest.isPending}
                          onClick={() => approveRequest.mutate({ clubId: clubId!, requestId: r.id })}
                          className="text-xs"
                        >
                          {t("joinRequest.approve")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={declineRequest.isPending && declineRequest.variables?.requestId === r.id}
                          disabled={approveRequest.isPending || declineRequest.isPending}
                          onClick={() => declineRequest.mutate({ clubId: clubId!, requestId: r.id })}
                          className="text-xs"
                          style={{ color: "var(--color-destructive)" }}
                        >
                          {t("joinRequest.decline")}
                        </Button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Invite panel — admins only */}
          {isAdmin && (
            <div>
              <button
                className="flex items-center gap-1 w-full text-left"
                onClick={() => setShowInvite((v) => !v)}
              >
                <p
                  className="text-xs font-medium uppercase tracking-widest"
                  style={{ color: "var(--color-accent)" }}
                >
                  {t("club.sections.inviteMembers")}
                </p>
                <span className="text-xs ml-auto" style={{ color: "var(--color-muted-foreground)" }}>
                  {showInvite ? "▲" : "▼"}
                </span>
              </button>
              {showInvite && (
                <div className="mt-3">
                  <InvitePanel clubId={clubId!} />
                </div>
              )}
            </div>
          )}
        </section>
        </div>

        {club.viewerRole && me && (
          <MeetingsSection
            clubId={clubId!}
            viewerId={me.id}
            isAdmin={isAdmin}
            clubBooks={(clubBooks ?? []).map(cb => ({ id: cb.id, title: cb.book?.title ?? "" }))}
          />
        )}

        <ClubDetailsSection
          isAdmin={isAdmin}
          isEditing={editingDetails}
          onEditStart={() => setEditingDetails(true)}
          onEditCancel={() => setEditingDetails(false)}
          onSave={({ description, type, locationCity, locationCountry }) => {
            setDescription.mutate({ clubId: clubId!, description });
            setType.mutate({ clubId: clubId!, type: type as "IN_PERSON" | "ONLINE" | "BOTH" | null });
            setLocation.mutate({ clubId: clubId!, locationCity, locationCountry });
            setEditingDetails(false);
          }}
          description={club.description ?? null}
          type={club.type ?? null}
          locationCity={club.locationCity ?? null}
          locationCountry={club.locationCountry ?? null}
        />
      </main>

      {showSearch && clubId && (
        <BookSearchDialog
          clubId={clubId}
          onClose={() => setShowSearch(false)}
          onAdded={() => setShowSearch(false)}
        />
      )}

      {promoteTarget && (
        <PromoteDialog
          open={true}
          bookTitle={promoteTarget.title}
          defaultPageCount={promoteTarget.pageCount}
          onConfirm={(canonicalPageCount, deadline) => {
            promote.mutate({ clubBookId: promoteTarget.id, canonicalPageCount, deadline });
            setPromoteTarget(null);
          }}
          onCancel={() => setPromoteTarget(null)}
        />
      )}

      {deadlineEditTarget && (
        <DeadlineEditDialog
          open={true}
          currentDeadline={deadlineEditTarget.deadline}
          onConfirm={(deadline) => {
            setDeadline.mutate({ clubBookId: deadlineEditTarget.id, deadline });
            setDeadlineEditTarget(null);
          }}
          onCancel={() => setDeadlineEditTarget(null)}
        />
      )}

      <ConfirmDialog
        open={confirmDialog !== null}
        title={confirmDialog?.title ?? ""}
        message={confirmDialog?.message}
        confirmLabel={t("common.confirm")}
        cancelLabel={t("common.cancel")}
        destructive={confirmDialog?.destructive}
        onConfirm={() => { confirmDialog?.onConfirm(); setConfirmDialog(null); }}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}
